import React from 'react';
import { useTranslation } from 'react-i18next';

import { TButtonTypes } from '@private/components';

import Button from 'components/Button';
import { ICONS_MAP } from 'constants/icons';
import { appReactMemo } from 'hocs';

import {
  ButtonsContainer,
  Container,
  Content,
  FooterStyled,
  HeaderStyled,
  StyledAbsoluteLoader,
  Title,
} from './styled';

export interface IModalContainerProps {
  children?: React.ReactNode;
  className?: string;
  customButtons?: React.ReactElement;
  disableLeftBtn?: boolean;
  disableRightBtn?: boolean;
  enableLoadingIndicator?: boolean;
  footer?: React.ReactElement;
  hideFooter?: boolean;
  isLoading?: boolean;
  leftBtnText?: string;
  leftBtnVariant?: 'outlined' | TButtonTypes;
  leftCopyText?: string;
  leftEnableCopyToClipboard?: boolean;
  onDismiss?: () => void;
  onLeftAction?: () => void;
  onRightAction?: () => void;
  rightBtnLoading?: boolean;
  rightBtnText?: string;
  rightBtnType?: React.ComponentProps<'button'>['type'];
  rightBtnVariant?: 'outlined' | TButtonTypes;
  rightCopyText?: string;
  rightEnableCopyToClipboard?: boolean;
  testIds?: {
    container?: string;
  };
  title?: string;
  titleLight?: boolean;
  width?: number;
}

const ModalContainer = ({
  children,
  className,
  customButtons,
  disableLeftBtn,
  disableRightBtn,
  enableLoadingIndicator = false,
  footer,
  hideFooter,
  isLoading = false,
  leftBtnText,
  leftBtnVariant = 'outlined',
  leftCopyText,
  leftEnableCopyToClipboard,
  onDismiss,
  onLeftAction,
  onRightAction,
  rightBtnLoading,
  rightBtnText,
  rightBtnType = 'submit',
  rightBtnVariant = 'primary',
  rightCopyText,
  rightEnableCopyToClipboard,
  testIds,
  title,
  titleLight,
  width,
}: IModalContainerProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'modals' });

  return (
    <Container className={className} data-testid={testIds?.container} width={width}>
      {enableLoadingIndicator && <StyledAbsoluteLoader height={4} show={isLoading} />}
      <HeaderStyled>
        <Title as={titleLight ? 'p' : 'h3'} size={titleLight ? 'xl' : undefined}>
          {title}
        </Title>
        <Button
          iconSize={12}
          onClick={onDismiss}
          startIcon={<ICONS_MAP.Close />}
          variant='icon'
        />
      </HeaderStyled>
      <Content>{children}</Content>
      {hideFooter || footer || (
        <FooterStyled>
          <ButtonsContainer>{customButtons || <div />}</ButtonsContainer>
          <ButtonsContainer>
            {onLeftAction && (
              <Button
                copyText={leftCopyText}
                disabled={disableLeftBtn}
                enableCopyToClipboard={leftEnableCopyToClipboard}
                hideCopyIcon
                onClick={onLeftAction}
                testIds={{ container: 'modalLeftButton' }}
                variant={leftBtnVariant}
              >
                {leftBtnText || t('close')}
              </Button>
            )}
            <Button
              copyText={rightCopyText}
              disabled={disableRightBtn}
              enableCopyToClipboard={rightEnableCopyToClipboard}
              hideCopyIcon
              isLoading={rightBtnLoading}
              onClick={onRightAction}
              testIds={{ container: 'modalRightButton' }}
              type={rightBtnType}
              variant={rightBtnVariant}
            >
              {rightBtnText || t('confirm')}
            </Button>
          </ButtonsContainer>
        </FooterStyled>
      )}
    </Container>
  );
};

export default appReactMemo(ModalContainer);
